<script lang="ts">
  import type { Snippet } from 'svelte';
  import X from '@lucide/svelte/icons/x';

  import { strings } from '../strings';
  import IconButton from './IconButton.svelte';

  interface Props {
    open?: boolean;
    title: string;
    description?: string;
    /** Clicking the backdrop closes the dialog (off for destructive confirmations). */
    closeOnBackdrop?: boolean;
    size?: 'sm' | 'md' | 'lg';
    /**
     * Where focus goes on close. Defaults to whatever was focused on open, which
     * Safari doesn't set for clicked buttons — pass the trigger to be safe.
     */
    returnFocusTo?: HTMLElement | null;
    onclose?: () => void;
    children?: Snippet;
    footer?: Snippet;
  }

  let {
    open = $bindable(false),
    title,
    description,
    closeOnBackdrop = true,
    size = 'md',
    returnFocusTo,
    onclose,
    children,
    footer,
  }: Props = $props();

  const id = $props.id();
  let dialog: HTMLDialogElement | undefined = $state();
  let returnFocus: HTMLElement | null = null;

  $effect(() => {
    if (!dialog) return;
    if (open && !dialog.open) {
      returnFocus = document.activeElement instanceof HTMLElement ? document.activeElement : null;
      dialog.showModal();
    } else if (!open && dialog.open) {
      dialog.close();
    }
  });

  function handleClose() {
    // Read the target first: `onclose` may clear the parent state it comes from.
    const target = returnFocusTo ?? returnFocus;
    returnFocus = null;
    open = false;
    onclose?.();
    if (target?.isConnected) target.focus();
  }

  function handleClick(event: MouseEvent) {
    // A click on the <dialog> element itself (not its content) is a click on the backdrop.
    if (closeOnBackdrop && event.target === dialog) dialog.close();
  }
</script>

<dialog
  bind:this={dialog}
  aria-modal="true"
  aria-labelledby="{id}-title"
  aria-describedby={description ? `${id}-description` : undefined}
  class={[
    'm-auto max-h-[min(90dvh,48rem)] w-[calc(100%-2rem)] rounded-card border border-line bg-surface p-0 text-ink shadow-xl',
    { sm: 'max-w-sm', md: 'max-w-lg', lg: 'max-w-2xl' }[size],
  ]}
  onclose={handleClose}
  onclick={handleClick}
>
  <div class="flex flex-col gap-4 p-5 sm:p-6">
    <div class="flex items-start justify-between gap-4">
      <div class="flex flex-col gap-1">
        <h2 id="{id}-title" class="text-lg font-semibold">{title}</h2>
        {#if description}
          <p id="{id}-description" class="text-sm text-ink-muted">{description}</p>
        {/if}
      </div>
      <IconButton
        label={strings.close}
        icon={X}
        size="sm"
        tooltip="none"
        class="-mt-1 -mr-2"
        onclick={() => dialog?.close()}
      />
    </div>
    {@render children?.()}
    {#if footer}
      <div class="flex flex-wrap justify-end gap-2 pt-2">
        {@render footer()}
      </div>
    {/if}
  </div>
</dialog>
